---
title: "Bulk import and export candidates"
description: "Import candidates into Chosen from a CSV or Excel file, bulk-upload resumes, and export candidate data as a CSV or a ZIP of resumes from the table."
category: "candidates"
keywords:
  - "import candidates CSV"
  - "bulk upload resumes"
  - "export candidates"
  - "candidate CSV export"
schema: "HowTo"
nav:
  section: "guides"
  order: 60
  parent: "/guides/candidates"
---

You can get candidates into Chosen in bulk — from a spreadsheet or a folder of resumes — and get data back out as a CSV or a batch of resume files. Imports map your columns to candidate fields; exports respect whatever filter you've got applied.

## Importing from a spreadsheet

A CSV or Excel file of candidates — from another system, an event, a sourcing list — imports in a few steps.

<Steps>
  <li>From the candidate list, choose to import and upload your CSV or Excel file.</li>
  <li>**Map columns.** Match each column in your file to a candidate field. Chosen guesses the obvious ones; you confirm or correct the rest.</li>
  <li>**Preview.** Check the sample of rows as they'll be imported. This is the moment to catch a column mapped to the wrong field.</li>
  <li>Import. The candidates are created with the values from your file.</li>
</Steps>

A spreadsheet import brings in whatever columns you mapped. It doesn't include resumes — for those, upload the files.

## Bulk-uploading resumes

If what you have is resumes rather than a spreadsheet, upload them directly. Bulk upload takes up to **50 files at once**. Each one becomes a candidate, and [resume parsing](/guides/candidates/resume-parsing) fills in their fields in the background — candidates show up immediately and their data populates as each file finishes.

## Exporting

Export from the top of the candidate table. Two formats:

- **CSV** — every parsed field plus status and owner, one row per candidate.
- **Resumes (ZIP)** — the original resume files, bundled.

Both respect the **current filter**. Filter the table to "engineering candidates who reached Interview" and the export contains exactly those — the filter bar is how you scope an export, so set it before you export.

<Callout type="info" title="Exports build in the background">
A CSV or resume ZIP isn't generated the instant you click. Chosen assembles the file in the background and the download is ready a moment later — quick for a small list, longer for a large one. You don't have to keep the page open and wait.
</Callout>

## Bulk actions on selected candidates

Select candidates with the checkboxes (or shift-click a range) and you can act on all of them at once — move them to a [status](/guides/candidates/statuses-and-pipeline), assign an owner, export just that selection, or reject them with a template.

**Reject with template** sends a templated rejection email to everyone selected. It goes from your own [connected Gmail inbox](/getting-started/integrations/gmail), not a generic `noreply@` — so replies come back to you, and candidates hear from a person.

## Limits

A spreadsheet import creates candidates from the columns you mapped; resumes are a separate upload. Bulk resume upload caps at 50 files per batch — for more, run it again. And reject-with-template needs Gmail connected; without it, that bulk action has no inbox to send from.
